import { useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import ListItemIcon from '@mui/material/ListItemIcon'
import Box from '@mui/system/Box'
import { useRouterStore } from '../../RouterStore'
import { useMainContainerStore } from '../MainContainerStore'
import Stack from '@mui/material/Stack'
import { themeConfig } from '@gap/shared/theme'
import ListItemButton from '@mui/material/ListItemButton'
import useStore from 'reusable/mobx-store-utils'
import { MenuChildItem } from '../MenuConfig'
import List from '@mui/material/List'
import { useLocation } from 'react-router-dom'
import MenuStore from './MenuStore'
import MenuIcon from '@mui/icons-material/Menu'
import Tooltip from '@mui/material/Tooltip'
import IconButton from "@mui/material/IconButton"
import MenuItem from '@mui/material/MenuItem'
import { NestedMenuItem } from 'reusable/nested-menu/NestedMenuItem'
import { useThemeStore } from 'theme/ThemeStore'

export default observer(function ReducedMenu() {
   const routerStore = useRouterStore()
   const mainContainerStore = useMainContainerStore()
   const themeStore = useThemeStore()

   return (
      <Stack sx={{ width: '80px', height: '100%', backgroundColor: themeStore.dark ? 'grey' : themeConfig.primary[800], color: 'white' }} role="presentation" onKeyDown={mainContainerStore.menuState.close}>
         {routerStore.menu.length >= 1 && <>
            <Box sx={{ flexShrink: 0, mt: 4, display: 'flex', justifyContent: 'center' }}>
               <MenuButton />
            </Box>

            <Box sx={{
               flex: 1,
               minHeight: 0,
               overflowY: 'auto',
               overflowX: 'hidden',
               ml: '-1.5px',
               mt: 4,
               '&::-webkit-scrollbar-thumb': {
                  boxShadow: 'inset 0 0 0 10px rgba(255, 255, 255, 0.16)',
               },
            }}>
               {routerStore.menu.map((menuParent, index) =>
                  <Box key={`menuItem-${index}`} >
                     {menuParent.title !== '' && <Box sx={{ mt: 4 }} />}
                     <List sx={{ mx: 1, py: 0 }}>
                        {menuParent.children.map((child, index) => child.children?.length
                           ? <ReducedMenuGroup child={child} key={`menuItemChild-${index}`} />
                           : <ReducedMenuItem child={child} key={`menuItemChild-${index}`} />)}
                     </List>
                  </Box>
               )}
            </Box>
         </>}
      </Stack>
   )
})

const MenuButton = observer(function MenuButton() {
   const mainContainerStore = useMainContainerStore()

   if (mainContainerStore.menuState.visible) {
      return null
   } else {
      return <IconButton sx={{
         color: 'white',
         borderRadius: '8px',
         ':hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.16)',
         }
      }} onClick={mainContainerStore.menuState.open}>
         <MenuIcon fontSize='medium' sx={{ cursor: 'pointer' }} />
      </IconButton>
   }
})

const ReducedMenuItem = observer(function ReducedMenuItem({ child }: { child: MenuChildItem }) {
   const mainContainerStore = useMainContainerStore()
   const routerStore = useRouterStore()
   const location = useLocation()

   const menuStore = useStore(sp => new MenuStore(sp), { child, path: location.pathname, routerStore, mainContainerStore })

   useEffect(() => {
      menuStore.updatePath(location.pathname)
      menuStore.setSxProps()
   }, [menuStore, location.pathname])

   return <ListItemButton sx={{
      ...menuStore.sxProps,
      mt: 0.5,
      width: '40px',
      height: '40px',
      p: 1,
      mx: 'auto',
      justifyContent: 'center',
      alignItems: 'center',
   }} onClick={menuStore.handleClick}>
      <Tooltip placement='right' title={child.title} PopperProps={{
         modifiers: [
            {
               name: "offset",
               options: { offset: [0, 8] } // Ensure the tooltip is right next to the menu, not overlapping and not to far away
            },
         ],
      }} >
         <ListItemIcon sx={{ minWidth: 0 }}><child.icon sx={{ color: 'white', height: '24px', width: '24px' }} /></ListItemIcon>
      </Tooltip>
   </ListItemButton>
})

// On the collapsed rail there's no room for an inline tree, so a group opens a flyout to the
// right (hover-triggered) listing its sub-items. Reuses the shared NestedMenuItem.
const ReducedMenuGroup = observer(function ReducedMenuGroup({ child }: { child: MenuChildItem }) {
   const mainContainerStore = useMainContainerStore()
   const routerStore = useRouterStore()
   const location = useLocation()

   const menuStore = useStore(sp => new MenuStore(sp), { child, path: location.pathname, routerStore, mainContainerStore })

   useEffect(() => {
      menuStore.updatePath(location.pathname)
      menuStore.setSxProps()
   }, [menuStore, location.pathname])

   return <NestedMenuItem
      parentMenuOpen
      label=""
      icon={<child.icon sx={{ color: 'white', height: '24px', width: '24px' }} />}
      containerSx={{ p: 0, width: '40px', mx: 'auto' }}
      sx={{
         ...menuStore.sxProps,
         mt: 0.5,
         width: '100%',
         height: '40px',
         p: 1,
         justifyContent: 'center',
         alignItems: 'center',
         borderRadius: '8px',
      }}
   >
      {child.children?.map(subItem =>
         <MenuItem
            key={subItem.navPath}
            data-nav-path={subItem.navPath}
            onClick={menuStore.navigateToSubItem}
            selected={menuStore.isSubItemSelected(subItem.navPath)}
         >
            {subItem.title}
         </MenuItem>
      )}
   </NestedMenuItem>
})
